import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const investorQuestions = [
  {
    question: "What does MyInvestFolio do?",
    answer: "MyInvestFolio helps you hold a serious portfolio together in one private, encrypted place. Continuity System — the entry tier — gives you a private record of every asset you own, plus a quiet safety net for the people you trust if you're ever unavailable. Portfolio Clarity sits on top and adds the full analytical view — portfolio allocation, performance, tax data, net worth and portfolio sharing."
  },
  {
    question: "Who is MyInvestFolio for?",
    answer: "MyInvestFolio is for discerning investors who want clarity, structure and control over their portfolios. Continuity System is for anyone who wants a private, encrypted record of what they own \u2014 with a quiet safety net for the people they trust. Portfolio Clarity is for those who additionally want the full analytical view \u2014 portfolio allocation, performance, tax data and sharing tools."
  },
  {
    question: "What's the difference between Continuity System and Portfolio Clarity?",
    answer: "Continuity System is the entry door \u2014 a private record of what you own, plus a quiet safety net for the people you trust. Portfolio Clarity is the full house \u2014 it includes everything in Continuity System, plus allocation charts, performance tracking, tax-year data and portfolio sharing. You can start with Continuity System and move up whenever you're ready."
  },
  {
    question: "How MyInvestFolio Works",
    answer: null,
    steps: [
      { title: "Choose Your Door", desc: "Sign yourself up for Continuity System, or request an invitation to Portfolio Clarity." },
      { title: "Record Your Assets", desc: "Enter your investments in plain language. Both tiers keep this encrypted; only you can see it." },
      { title: "Set Up Your Safety Net (Continuity System)", desc: "Add Trusted Contacts and any documents you\u2019d like them to reach if you\u2019re ever unavailable." },
      { title: "Unlock the Full Picture (Portfolio Clarity)", desc: "Organize your holdings across direct and indirect classes, including entities and trusts. View your entire portfolio in one structured dashboard and see asset allocation and performance metrics." },
      { title: "Refine Over Time", desc: "Update whenever your portfolio changes. You stay in control." }
    ]
  },
  {
    question: "Do I need to enter all my past investments?",
    answer: "No. Start with what matters most today. On Continuity System, even one asset with a Trusted Contact is already a real safety net. On Portfolio Clarity, the record grows with you. Simple to begin, powerful as you grow."
  },
  {
    question: "Can I try it without inputting real investments?",
    answer: "Yes. You can start with fictional or partial data to see how the platform works, and switch to real investments whenever you\u2019re ready. Please avoid naming Trusted Contacts against fictional assets \u2014 save your Trusted Contact setup for real records."
  },
  {
    question: "Can MyInvestFolio see my investment data?",
    answer: "No. Your portfolio is encrypted and only accessible by you. Your data, your control. Even MyInvestFolio team cannot view your data, unless you explicitly grant access for support."
  },
  {
    question: "What happens if there's a security breach?",
    answer: "Data is scrambled and unreadable without your keys. We also maintain bank-grade encryption and anonymity."
  }
];

const CommonInvestorQuestions = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (idx) => setOpenIndex(openIndex === idx ? null : idx);

  return (
    <section id="investor-questions" className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
      <div className="text-center mb-8 sm:mb-12">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-slate-900">
          Common Investor Questions
        </h2>
        <p className="text-base sm:text-lg text-slate-600 mt-3 max-w-2xl mx-auto">
          Everything you need to know before getting started.
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-3">
        {investorQuestions.map((item, idx) => (
          <div
            key={`q-${idx}`}
            data-testid={`investor-faq-${idx}`}
            className="border border-slate-200 rounded-xl overflow-hidden bg-white"
          >
            <button
              onClick={() => toggle(idx)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition"
            >
              <span className="font-semibold text-slate-800 text-sm sm:text-base pr-4">{item.question}</span>
              <ChevronDown
                className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${openIndex === idx ? 'rotate-180' : ''}`}
              />
            </button>
            {openIndex === idx && (
              <div className="px-5 pb-4 text-sm sm:text-base text-slate-600 leading-relaxed">
                {item.answer && <p>{item.answer}</p>}
                {item.steps && (
                  <div className="space-y-4 mt-1">
                    {item.steps.map((step, sIdx) => (
                      <div key={`step-${step.title}`} className="flex gap-3">
                        <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold mt-0.5">
                          {sIdx + 1}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{step.title}</p>
                          <p className="text-slate-600 mt-0.5">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default CommonInvestorQuestions;
